import React, { useState, useRef, useCallback } from 'react';
import { LockIcon, AISparkIcon, SendIcon, CheckIcon } from './PanelIcons';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import AudioBubble from '../chat/AudioBubble';

// ── Section button bar — 3 rows, English labels ────────────────────────────────
const SECTION_BTNS = [
  [
    { id: 'panchanga',    label: 'Panchanga' },
    { id: 'personality',  label: 'Personality' },
    { id: 'moles',        label: 'Moles & Palm' },
    { id: 'childhood',    label: 'Childhood' },
  ],
  [
    { id: 'teenage',      label: 'Teenage' },
    { id: 'youngAdult',   label: 'Young Adult' },
    { id: 'recentYears',  label: 'Recent Years' },
    { id: 'loveMarriage', label: 'Love & Marriage' },
  ],
  [
    { id: 'career',  label: 'Career' },
    { id: 'health',  label: 'Health' },
    { id: 'overall', label: 'Overall' },
    { id: 'future',  label: 'Future ✨', isFutureToggle: true },
  ],
];

// ── Future topic button grid ───────────────────────────────────────────────────
const FUTURE_TOPICS = [
  [
    { id: 'currentPhase',  label: '⚡ Current Phase' },
    { id: 'next1year',     label: '📅 Next 1 Year' },
    { id: 'next3years',    label: '🗓 Next 3 Years' },
  ],
  [
    { id: 'careerFuture',    label: '💼 Career' },
    { id: 'marriageFuture',  label: '💑 Marriage' },
    { id: 'financeFuture',   label: '💰 Finance' },
  ],
  [
    { id: 'healthFuture',    label: '🏥 Health' },
    { id: 'tenYearOverview', label: '🌟 10 Years' },
    { id: 'specificQuestion',label: '❓ Ask Question' },
  ],
];

// ── 5-language options ────────────────────────────────────────────────────────
const LANGUAGES = [
  { id: 'telugu',       label: 'తెలుగు',      tag: 'తె' },
  { id: 'telugu-roman', label: 'Telugu-Roman', tag: 'TR' },
  { id: 'hindi',        label: 'हिंदी',       tag: 'हि' },
  { id: 'hindi-roman',  label: 'Hindi-Roman',  tag: 'HR' },
  { id: 'english',      label: 'English',      tag: 'EN' },
];

function detectConfirmation(text) {
  const lower = text.toLowerCase().trim();
  return lower.startsWith('client confirmed:') ||
         lower.startsWith('client confirmed ') ||
         lower.startsWith('confirmed:');
}

function clean(text) {
  if (!text) return '';
  return text.replace(/\*\*\*/g, '').replace(/\*\*/g, '').replace(/\*/g, '')
             .replace(/#{1,6}\s?/g, '').trim();
}

// ── Render text as bullets or paragraphs ─────────────────────────────────────
function renderBullets(text) {
  if (!text) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  // Check if the block has any bullet lines
  const hasBullets = lines.some(l => /^[-•*]\s/.test(l));

  if (!hasBullets) {
    // Plain paragraph
    return <p className="ap-bubble-para">{text}</p>;
  }

  // Mixed: bullets and non-bullet intro lines
  return (
    <div className="ap-bullet-body">
      {lines.map((line, i) => {
        if (/^[-•*]\s/.test(line)) {
          const content = line.replace(/^[-•*]\s+/, '');
          // Split on em-dash or double-dash for trait — reason layout
          const dashIdx = content.search(/ [—–-]{1,2} /);
          if (dashIdx !== -1) {
            const trait  = content.slice(0, dashIdx).trim();
            const reason = content.slice(dashIdx).replace(/^ [—–-]{1,2} /, '').trim();
            return (
              <div key={i} className="ap-bullet-item">
                <span className="ap-bullet-dot">◆</span>
                <span>
                  <span className="ap-bullet-trait">{trait}</span>
                  <span className="ap-bullet-reason"> — {reason}</span>
                </span>
              </div>
            );
          }
          return (
            <div key={i} className="ap-bullet-item">
              <span className="ap-bullet-dot">◆</span>
              <span>{content}</span>
            </div>
          );
        }
        return <p key={i} className="ap-bullet-intro">{line}</p>;
      })}
    </div>
  );
}

// ── Helpers for PanchangaCard ─────────────────────────────────────────────────
const PLANET_TE_PC = {
  Sun:'సూర్యుడు', Moon:'చంద్రుడు', Mars:'కుజుడు', Mercury:'బుధుడు',
  Jupiter:'గురువు', Venus:'శుక్రుడు', Saturn:'శని', Rahu:'రాహువు',
  Ketu:'కేతువు', Ascendant:'లగ్నం',
};
const SIGN_TE_PC = {
  Aries:'మేషం', Taurus:'వృషభం', Gemini:'మిథునం', Cancer:'కర్కాటకం',
  Leo:'సింహం', Virgo:'కన్య', Libra:'తులా', Scorpio:'వృశ్చికం',
  Sagittarius:'ధనుస్సు', Capricorn:'మకరం', Aquarius:'కుంభం', Pisces:'మీనం',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${MONTHS[parseInt(m,10)-1]} ${y}`;
}

// ── Panchanga info card — 4 tabs ──────────────────────────────────────────────
const PanchangaCard = ({ entry }) => {
  const [tab,  setTab]  = useState('panch');
  const [lang, setLang] = useState(entry.telugu ? 'te' : 'en');
  const [antarOpen, setAntarOpen] = useState(null); // planet string of expanded antar

  const parseRows = text =>
    (text || '').split('\n')
      .map(line => { const i = line.indexOf(': '); return i !== -1 ? { label: line.slice(0,i), value: line.slice(i+2) } : null; })
      .filter(Boolean);

  const panchRows = parseRows(lang === 'te' ? entry.telugu : entry.english);
  const d1  = entry.d1Chart   || [];
  const d9  = entry.d9Chart   || [];
  const dasha = entry.dashaFull || null;

  // Planets to show (exclude Ascendant from D9)
  const d1Planets = d1.filter(p => p.name !== 'Ascendant');
  const d9Planets = d9.filter(p => p.name !== 'Ascendant');

  const TABS = [
    { id: 'panch', label: 'Panchanga' },
    { id: 'd1',    label: 'D1 Chart'  },
    { id: 'd9',    label: 'D9 Chart'  },
    { id: 'dasha', label: 'Dasha'     },
  ];

  const cur   = dasha?.current;
  const maha  = cur?.maha_dasha;
  const antar = cur?.antar_dasha;
  const praty = cur?.pratyantar_dasha;

  return (
    <div className="ap-panchanga-card">

      {/* ── Card header ── */}
      <div className="ap-panchanga-card__header">
        <div className="ap-panchanga-card__titles">
          <span className="ap-panchanga-card__title">✦ పంచాంగ వివరాలు</span>
          <span className="ap-panchanga-card__sub">BIRTH PANCHANGA</span>
        </div>
      </div>

      {/* ── 4-tab bar ── */}
      <div className="ap-panch-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`ap-panch-tab${tab === t.id ? ' ap-panch-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >{t.label}</button>
        ))}
      </div>

      {/* ══ TAB: Panchanga ══ */}
      {tab === 'panch' && (
        <>
          <div className="ap-bubble-tabs ap-bubble-tabs--compact" style={{padding:'0.4rem 0.85rem 0'}}>
            <button className={`ap-bubble-tab${lang==='te'?' ap-bubble-tab--active':''}`} onClick={()=>setLang('te')}>తె</button>
            <button className={`ap-bubble-tab${lang==='en'?' ap-bubble-tab--active':''}`} onClick={()=>setLang('en')}>EN</button>
          </div>
          <table className="ap-panchanga-card__table">
            <tbody>
              {panchRows.map((row,i) => (
                <tr key={i} className="ap-panchanga-card__row">
                  <td className="ap-panchanga-card__label">{row.label}</td>
                  <td className="ap-panchanga-card__value">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ══ TAB: D1 Chart ══ */}
      {tab === 'd1' && (
        <div className="ap-chart-table-wrap">
          {d1.length === 0
            ? <p className="ap-chart-empty">D1 data not available — click Analyze Chart first</p>
            : (
              <table className="ap-chart-table">
                <thead>
                  <tr>
                    <th>Planet</th>
                    <th>Sign</th>
                    <th>H</th>
                    <th>Nakshatra</th>
                    <th>Pada</th>
                    <th>R</th>
                  </tr>
                </thead>
                <tbody>
                  {d1.map((pl, i) => (
                    <tr key={i} className={pl.name === 'Ascendant' ? 'ap-chart-row--asc' : ''}>
                      <td className="ap-chart-planet">{pl.name}</td>
                      <td>{pl.sign}</td>
                      <td className="ap-chart-house">{pl.house}</td>
                      <td className="ap-chart-nak">{pl.nakshatra}</td>
                      <td>{pl.pada}</td>
                      <td>{pl.isRetro ? 'R' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {/* ══ TAB: D9 Chart ══ */}
      {tab === 'd9' && (
        <div className="ap-chart-table-wrap">
          {d9Planets.length === 0
            ? <p className="ap-chart-empty">D9 data not available — click Analyze Chart first</p>
            : (
              <table className="ap-chart-table">
                <thead>
                  <tr>
                    <th>Planet</th>
                    <th>D1 Sign</th>
                    <th>D9 Sign</th>
                    <th>D9 House</th>
                  </tr>
                </thead>
                <tbody>
                  {d9Planets.map((pl, i) => {
                    const d1pl = d1.find(p => p.name === pl.name);
                    return (
                      <tr key={i}>
                        <td className="ap-chart-planet">{pl.name}</td>
                        <td>{d1pl?.sign || '—'}</td>
                        <td className="ap-chart-d9sign">{pl.d9Sign}</td>
                        <td className="ap-chart-house">{pl.d9House || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          }
          {/* D9 Ascendant */}
          {d9.find(p => p.name === 'Ascendant') && (
            <div className="ap-d9-asc">
              D9 Lagna: <strong>{d9.find(p=>p.name==='Ascendant').d9Sign}</strong>
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: Dasha ══ */}
      {tab === 'dasha' && (
        <div className="ap-dasha-tab">

          {/* Current period */}
          {cur && (
            <div className="ap-dasha-current">
              <div className="ap-dasha-current__title">▸ Current Dasha Period</div>
              <table className="ap-dasha-cur-table">
                <tbody>
                  {maha && (
                    <tr>
                      <td className="ap-dc-label">Mahadasha</td>
                      <td className="ap-dc-planet">{maha.planet}</td>
                      <td className="ap-dc-dates">ends {fmtDate(maha.ends)}</td>
                    </tr>
                  )}
                  {antar && (
                    <tr>
                      <td className="ap-dc-label">Antardasha</td>
                      <td className="ap-dc-planet">{antar.planet}</td>
                      <td className="ap-dc-dates">{fmtDate(antar.starts)} – {fmtDate(antar.ends)}</td>
                    </tr>
                  )}
                  {praty && (
                    <tr>
                      <td className="ap-dc-label">Pratyantar</td>
                      <td className="ap-dc-planet ap-dc-planet--praty">{praty.planet}</td>
                      <td className="ap-dc-dates">{fmtDate(praty.starts)} – {fmtDate(praty.ends)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Antardasha list within current Mahadasha */}
          {dasha?.antarList?.length > 0 && (
            <div className="ap-dasha-section">
              <div className="ap-dasha-section__title">
                Antardasha — {maha?.planet} Mahadasha
              </div>
              <table className="ap-dasha-list-table">
                <thead><tr><th>Antardasha</th><th>From</th><th>To</th><th></th></tr></thead>
                <tbody>
                  {dasha.antarList.map((a, i) => (
                    <React.Fragment key={i}>
                      <tr
                        className={`ap-dasha-row${a.isCurrent ? ' ap-dasha-row--current' : ''}`}
                        onClick={() => setAntarOpen(antarOpen === a.planet + i ? null : a.planet + i)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="ap-dasha-planet">{a.isCurrent ? '▶ ' : ''}{a.planet}</td>
                        <td>{fmtDate(a.starts)}</td>
                        <td>{fmtDate(a.ends)}</td>
                        <td>{antarOpen === a.planet+i ? '▲' : '▼'}</td>
                      </tr>
                      {antarOpen === a.planet + i && a.pratyantars?.map((pr, j) => (
                        <tr key={j} className={`ap-dasha-praty${pr.isCurrent ? ' ap-dasha-praty--current' : ''}`}>
                          <td className="ap-dasha-praty-planet">↳ {pr.planet}</td>
                          <td>{fmtDate(pr.starts)}</td>
                          <td>{fmtDate(pr.ends)}</td>
                          <td></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* All Mahadasha timeline */}
          {dasha?.allMaha?.length > 0 && (
            <div className="ap-dasha-section">
              <div className="ap-dasha-section__title">Mahadasha Timeline</div>
              <table className="ap-dasha-list-table">
                <thead><tr><th>Mahadasha</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  {dasha.allMaha.map((m, i) => {
                    const today = new Date().toISOString().split('T')[0];
                    const isCur = m.starts <= today && m.ends >= today;
                    return (
                      <tr key={i} className={`ap-dasha-row${isCur ? ' ap-dasha-row--current' : ''}`}>
                        <td className="ap-dasha-planet">{isCur ? '▶ ' : ''}{m.planet}</td>
                        <td>{fmtDate(m.starts)}</td>
                        <td>{fmtDate(m.ends)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!cur && !dasha?.allMaha?.length && (
            <p className="ap-chart-empty">Dasha data not available — click Analyze Chart first</p>
          )}
        </div>
      )}

    </div>
  );
};

// ── Period card (one antardasha) — collapsed by default ──────────────────────
const PeriodCard = ({ period }) => {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab]           = useState(period.telugu ? 'te' : 'en');

  const label = period.ageFrom != null
    ? `${period.planet} | Age ${period.ageFrom}${period.ageTo != null && period.ageTo !== period.ageFrom ? `–${period.ageTo}` : ''}`
    : period.planet;

  return (
    <div className="ap-period-card">
      <button className="ap-period-card__header" onClick={() => setExpanded(e => !e)}>
        <span className="ap-period-card__title">{label}</span>
        <span className="ap-period-card__toggle">{expanded ? '▲' : '▼'}</span>
      </button>

      {/* Events always visible */}
      {period.events.length > 0 && (
        <div className="ap-period-card__events">
          {period.events.map((ev, i) => (
            <div key={i} className="ap-period-card__event">• {ev}</div>
          ))}
        </div>
      )}

      {/* Full detail shown when expanded */}
      {expanded && (period.telugu || period.english) && (
        <div className="ap-period-card__detail">
          {period.telugu && (
            <div className="ap-bubble-tabs ap-bubble-tabs--compact">
              <button
                className={`ap-bubble-tab${tab === 'te' ? ' ap-bubble-tab--active' : ''}`}
                onClick={() => setTab('te')}>
                తెలుగు
              </button>
              <button
                className={`ap-bubble-tab${tab === 'en' ? ' ap-bubble-tab--active' : ''}`}
                onClick={() => setTab('en')}>
                English
              </button>
            </div>
          )}
          {tab === 'te' && period.telugu && (
            <p className="ap-period-card__text ap-chat-bubble__telugu">{clean(period.telugu)}</p>
          )}
          {(tab === 'en' || !period.telugu) && period.english && (
            <p className="ap-period-card__text ap-chat-bubble__english">{clean(period.english)}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Current dasha card shown at top of Future mode ───────────────────────────
const FutureDashaCard = ({ dashaData }) => {
  const maha       = dashaData?.current?.maha_dasha?.planet  || '—';
  const antar      = dashaData?.current?.antar_dasha?.planet || '—';
  const antarEndRaw = dashaData?.current?.antar_dasha?.end;
  const antarEnd   = antarEndRaw
    ? new Date(antarEndRaw).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
    : null;
  return (
    <div className="ap-future-dasha-card">
      <div className="ap-future-dasha-card__left">
        <div className="ap-future-dasha-card__label">Active Dasha</div>
        <div className="ap-future-dasha-card__main">
          <span className="ap-future-dasha-card__maha">{maha} MD</span>
          <span className="ap-future-dasha-card__sep"> › </span>
          <span className="ap-future-dasha-card__antar">{antar} AD</span>
        </div>
      </div>
      {antarEnd && (
        <div className="ap-future-dasha-card__right">
          <div className="ap-future-dasha-card__end-label">AD ends</div>
          <div className="ap-future-dasha-card__end-val">{antarEnd}</div>
        </div>
      )}
    </div>
  );
};

// ── Follow-up label → topic id map ────────────────────────────────────────────
const FOLLOWUP_TO_TOPIC = {
  'Next 1 Year':             'next1year',
  'Next 3 Years':            'next3years',
  'Career Future':           'careerFuture',
  'Marriage':                'marriageFuture',
  'Finance':                 'financeFuture',
  'Remedies':                'remedies',
  'Current Phase':           'currentPhase',
  '10 Year Overview':        'tenYearOverview',
  'Health':                  'healthFuture',
  'Book Remedy Consultation':'remedies',
};

// ── Small language tag shown on each bubble ───────────────────────────────────
const LANG_TAG = { telugu: 'తె', english: 'EN', 'telugu-roman': 'TR', hindi: 'हि', 'hindi-roman': 'HR' };

// ── Structured AI bubble — summary + period cards + traits + verify ───────────
const AiBubble = ({ entry, onVerify, onRetranslate, onFutureTopic }) => {
  // Default tab: English for non-Telugu/English langs, Telugu if available, else English
  const [tab, setTab] = useState(() => {
    if (entry.lang === 'english' || entry.lang === 'telugu-roman' || entry.lang === 'hindi' || entry.lang === 'hindi-roman') return 'en';
    return entry.telugu ? 'te' : 'en';
  });

  const hasPeriods = Array.isArray(entry.periods) && entry.periods.length > 0;
  const hasTraits  = Array.isArray(entry.traits)  && entry.traits.length  > 0;
  const hasEvents  = !hasPeriods && Array.isArray(entry.events) && entry.events.length > 0;
  const hasVerify  = Array.isArray(entry.verify)  && entry.verify.length  > 0;
  const conf       = entry.confidence;
  const hasText    = entry.telugu || entry.english;

  return (
    <div className="ap-chat-bubble ap-chat-bubble--ai">

      {/* ── Language tag (shows which language this response was generated in) ── */}
      {entry.lang && (
        <span className="ap-bubble-lang-tag">{LANG_TAG[entry.lang] || entry.lang}</span>
      )}

      {/* ── Language toggle + confidence ── */}
      {hasText && (
        <div className="ap-bubble-tabs">
          {entry.telugu && (
            <button
              className={`ap-bubble-tab${tab === 'te' ? ' ap-bubble-tab--active' : ''}`}
              onClick={() => setTab('te')}>
              తెలుగు
            </button>
          )}
          {entry.english && (
            <button
              className={`ap-bubble-tab${tab === 'en' ? ' ap-bubble-tab--active' : ''}`}
              onClick={() => setTab('en')}>
              English
            </button>
          )}
          {conf != null && <span className="ap-bubble-conf">{conf}%</span>}
        </div>
      )}

      {/* ── Content — only one language at a time ── */}
      {tab === 'te' && entry.telugu && (
        <div className="ap-chat-bubble__telugu">
          {renderBullets(clean(entry.telugu))}
        </div>
      )}
      {tab === 'en' && entry.english && (
        <div className="ap-chat-bubble__english">
          {renderBullets(clean(entry.english))}
        </div>
      )}

      {/* ── Period cards (PHASE BREAKDOWN) ── */}
      {hasPeriods && (
        <div className="ap-period-cards">
          <div className="ap-period-cards__label">Phase Breakdown</div>
          {entry.periods.map((period, i) => (
            <PeriodCard key={i} period={period} />
          ))}
        </div>
      )}

      {/* ── Legacy events timeline (fallback for sections without PHASE BREAKDOWN) ── */}
      {hasEvents && (
        <div className="ap-bubble-events">
          <div className="ap-bubble-events__title">Events Timeline</div>
          {entry.events.map((ev, i) => (
            <div key={i} className="ap-bubble-event">
              <span className="ap-bubble-event__age">
                Age {ev.ageFrom}{ev.ageTo && ev.ageTo !== ev.ageFrom ? `–${ev.ageTo}` : ''}
              </span>
              <span className="ap-bubble-event__text">{ev.event}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Overall traits ── */}
      {hasTraits && (
        <div className="ap-bubble-traits">
          <div className="ap-bubble-traits__title">Key Traits</div>
          <ul className="ap-bubble-traits__list">
            {entry.traits.map((t, i) => (
              <li key={i} className="ap-bubble-traits__item">{t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Verify questions ── */}
      {hasVerify && (
        <div className="ap-bubble-verify">
          <div className="ap-bubble-verify__title">Verify with client:</div>
          {entry.verify.map((q, i) => {
            const qText = typeof q === 'string' ? q : (q?.question || q?.text || '');
            return (
              <div key={i} className="ap-bubble-verify__row">
                <span className="ap-bubble-verify__q">{qText}</span>
                <div className="ap-bubble-verify__btns">
                  <button className="ap-vbtn ap-vbtn--yes"
                    onClick={() => onVerify?.(entry.id, i, 'yes', qText)}
                    title="Yes — confirmed">✓ Yes</button>
                  <button className="ap-vbtn ap-vbtn--partial"
                    onClick={() => onVerify?.(entry.id, i, 'partial', qText)}
                    title="Partial match">~ Maybe</button>
                  <button className="ap-vbtn ap-vbtn--no"
                    onClick={() => onVerify?.(entry.id, i, 'no', qText)}
                    title="No — not accurate">✗ No</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Retranslation pills — re-fetch section in chosen language ── */}
      {entry.section && entry.section !== 'panchanga' && (
        <div className="ap-bubble-retrans">
          {LANGUAGES.map(lang => (
            <button
              key={lang.id}
              className={[
                'ap-retrans-pill',
                entry.lang === lang.id ? 'ap-retrans-pill--active' : '',
                entry.retransLoading   ? 'ap-retrans-pill--loading' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => !entry.retransLoading && onRetranslate?.(entry.id, entry.section, lang.id)}
              disabled={entry.retransLoading}
              title={`Retranslate to ${lang.label}`}
            >
              {lang.tag}
            </button>
          ))}
        </div>
      )}

      {/* ── Follow-up suggestions (future mode bubbles) ── */}
      {Array.isArray(entry.followUp) && entry.followUp.length > 0 && onFutureTopic && (
        <div className="ap-followup-bar">
          <div className="ap-followup-bar__label">Next topic:</div>
          <div className="ap-followup-bar__btns">
            {entry.followUp.map((label, i) => {
              const topicId = FOLLOWUP_TO_TOPIC[label] || label;
              return (
                <button
                  key={i}
                  className="ap-followup-btn"
                  onClick={() => onFutureTopic(topicId)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

// ── Mobile palm photo upload widget (palmistry only) ─────────────────────────
const MobilePalmUpload = ({ onUpload, rightUrl, leftUrl }) => {
  const rCamRef = useRef(null);
  const rGalRef = useRef(null);
  const lCamRef = useRef(null);
  const lGalRef = useRef(null);

  const handleFile = (e, hand) => {
    const file = e.target.files?.[0];
    if (file && onUpload) onUpload(file, hand);
    e.target.value = ''; // reset so same file can be re-selected
  };

  return (
    <div className="ap-mobile-palm-upload" onClick={e => e.stopPropagation()}>
      {/* RIGHT HAND */}
      <div className="ap-mobile-palm-upload__section">
        <div className="ap-mobile-palm-upload__label">
          ✋ Right Hand <span className="ap-mobile-palm-upload__sub">(Present Life)</span>
          {rightUrl && <span className="ap-mobile-palm-upload__ok">✓</span>}
        </div>
        <div className="ap-mobile-palm-upload__btns">
          <label className="palm-camera-btn">
            📷 Camera
            <input ref={rCamRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={e => handleFile(e, 'right')} />
          </label>
          <label className="palm-gallery-btn">
            🖼 Gallery
            <input ref={rGalRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleFile(e, 'right')} />
          </label>
        </div>
        {rightUrl && <img src={rightUrl} className="palm-preview" alt="Right hand palm" />}
      </div>

      {/* LEFT HAND */}
      <div className="ap-mobile-palm-upload__section">
        <div className="ap-mobile-palm-upload__label">
          🤚 Left Hand <span className="ap-mobile-palm-upload__sub">(Birth Potential)</span>
          {leftUrl && <span className="ap-mobile-palm-upload__ok">✓</span>}
        </div>
        <div className="ap-mobile-palm-upload__btns">
          <label className="palm-camera-btn">
            📷 Camera
            <input ref={lCamRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={e => handleFile(e, 'left')} />
          </label>
          <label className="palm-gallery-btn">
            🖼 Gallery
            <input ref={lGalRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleFile(e, 'left')} />
          </label>
        </div>
        {leftUrl && <img src={leftUrl} className="palm-preview" alt="Left hand palm" />}
      </div>
    </div>
  );
};

// ── Mobile client info card (shown after analyze) ────────────────────────────
const MobileClientCard = ({ clientData, aiData, rightHandUrl, leftHandUrl }) => {
  const [expanded, setExpanded] = useState(true);
  if (!clientData) return null;

  const dob   = clientData.dob  || '';
  const tob   = clientData.tob  || '';
  const place = clientData.pob  || '';
  const maha  = aiData?.dashaData?.current?.maha_dasha?.planet;
  const antar = aiData?.dashaData?.current?.antar_dasha?.planet;

  return (
    <div
      className={`ap-mobile-client-card${expanded ? ' ap-mobile-client-card--expanded' : ''}`}
      onClick={() => setExpanded(v => !v)}
    >
      <div className="ap-mobile-client-card__row">
        <span className="ap-mobile-client-card__name">
          {clientData.name}{clientData.gender ? ` · ${clientData.gender}` : ''}{clientData.language ? ` · ${clientData.language}` : ''}
        </span>
        <span className="ap-mobile-client-card__toggle">{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <>
          {(dob || tob) && (
            <div className="ap-mobile-client-card__detail">
              {dob}{tob ? ` · ${tob}` : ''}
            </div>
          )}
          {place && (
            <div className="ap-mobile-client-card__detail">{place}</div>
          )}
          {(maha || antar) && (
            <div className="ap-mobile-client-card__dasha">
              {maha ? `${maha} MD` : ''}{maha && antar ? ' · ' : ''}{antar ? `${antar} AD` : ''}
            </div>
          )}
          {(rightHandUrl || leftHandUrl) && (
            <div className="ap-mobile-client-card__palms">
              {rightHandUrl && (
                <div className="ap-mobile-palm-thumb" title="Right Hand">
                  <img src={rightHandUrl} alt="Right palm" />
                  <span>✋R</span>
                </div>
              )}
              {leftHandUrl && (
                <div className="ap-mobile-palm-thumb" title="Left Hand">
                  <img src={leftHandUrl} alt="Left palm" />
                  <span>🤚L</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Main AiChatbot Component ──────────────────────────────────────────────────
const AiChatbot = ({
  aiData,
  chatHistory,
  chatLoading,
  chatError,
  chatQuestion,
  setChatQuestion,
  onSend,              // (quickQ, sectionId, mode, confirmedEvents) => void
  onClearChat,
  onVerify,            // (bubbleId, qIdx, answer, questionText) => void (optional)
  chatHistoryRef,
  sectionBtnActive,    // currently active section id
  sectionBtnDone,      // Set of completed section ids
  onSectionBtnClick,   // (sectionId) => void
  analysisReady,       // bool — show buttons only after chart is loaded
  globalLang,          // 'telugu' | 'english' | 'telugu-roman' | 'hindi' | 'hindi-roman'
  onLangChange,        // (lang) => void
  onRetranslate,       // (bubbleId, sectionId, lang) => void
  onFutureTopic,       // (topicId, customQuestion?) => void
  serviceButtons,      // array of {id, label, type?} — replaces built-in button grid when provided
  serviceMode,         // service mode string — used for routing free-text input when serviceButtons present
  onMobileAnalyze,     // mobile-only: trigger chart analysis
  analyzeLoading,      // mobile-only: analysis in progress
  analyzeError,        // mobile-only: analysis error message
  clientData,          // mobile-only: for client card display
  rightHandPreviewUrl, // mobile-only: right hand thumbnail (palmistry)
  leftHandPreviewUrl,  // mobile-only: left hand thumbnail (palmistry)
  onMobilePalmUpload,  // mobile-only: palmistry upload handler (file, hand) => void
  onSendAudio,         // (audioUrl) => void — send voice message
}) => {
  const [mode, setMode] = useState('past');
  const inputRef = useRef(null);
  const { isRecording: isAudioRec, duration: audioDuration, analyserData: audioWave, start: startAudioRec, stop: stopAudioRec, cancel: cancelAudioRec } = useAudioRecorder();

  const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;

  const handleMicClick = useCallback(async () => {
    if (isAudioRec) return;
    try { await startAudioRec(); } catch (e) { console.error('Mic access denied:', e); }
  }, [isAudioRec, startAudioRec]);

  const handleAudioSend = useCallback(async () => {
    const blob = await stopAudioRec();
    if (!blob || blob.size === 0) return;
    const form = new FormData();
    form.append('file', blob, `voice_${Date.now()}.webm`);
    try {
      const resp = await fetch(`${API_BASE}/chat/upload`, { method: 'POST', body: form });
      const data = await resp.json();
      if (data.success && data.url) onSendAudio?.(data.url);
    } catch (e) { console.error('Audio upload failed:', e); }
  }, [stopAudioRec, onSendAudio, API_BASE]);

  const handleSend = () => {
    const text = chatQuestion.trim();
    if (!text) return;
    const effectiveMode = serviceMode || mode;
    if (effectiveMode === 'future') {
      setChatQuestion('');
      onFutureTopic?.('specificQuestion', text);
    } else {
      const confirmedEvts = detectConfirmation(text) ? text : null;
      onSend(text, 'general', effectiveMode, confirmedEvts);
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const switchMode = newMode => {
    if (newMode === mode) return;
    setMode(newMode);
    onClearChat();
  };

  return (
    <main className="ap-col ap-col--center ap-chatbot">

      {/* ── Header ── */}
      <div className="ap-chatbot__header">
        <div className="ap-chatbot__title">
          <AISparkIcon /> VTD AI Astrologer
        </div>
        <div className="ap-chatbot__header-right">
          <span className="ap-private-badge"><LockIcon /> Private</span>
          {chatHistory.length > 0 && (
            <button className="ap-chatbot__clear" onClick={onClearChat}>Clear</button>
          )}
        </div>
      </div>

      {/* ── Mode toggle — hidden when serviceButtons provided ── */}
      {!serviceButtons && (
        <div className="ap-mode-toggle">
          <button
            className={`ap-mode-btn${mode === 'past' ? ' ap-mode-btn--active' : ''}`}
            onClick={() => switchMode('past')}>
            Past Prediction
          </button>
          <button
            className={`ap-mode-btn${mode === 'future' ? ' ap-mode-btn--active' : ''}`}
            onClick={() => switchMode('future')}>
            Future Consultation
          </button>
        </div>
      )}

      {/* ── Active dasha strip ── */}
      {aiData && (
        <div className="ap-dasha-box">
          <span className="ap-dasha-box__icon">☿</span>
          <div>
            <div className="ap-dasha-box__label">Active Dasha Period</div>
            <div className="ap-dasha-box__value">
              {aiData.dashaData?.current?.maha_dasha?.planet  || '—'} Mahadasha
              {' · '}
              {aiData.dashaData?.current?.antar_dasha?.planet || '—'} Antardasha
            </div>
          </div>
        </div>
      )}

      {/* ── Service-specific button grid (replaces built-in past/future layout) ── */}
      {serviceButtons && (
        <div className="ap-service-btns">
          {serviceButtons.map(btn => {
            const isDone   = sectionBtnDone?.has(btn.id);
            const isActive = sectionBtnActive === btn.id;
            return (
              <button
                key={btn.id}
                className={[
                  'ap-service-btn',
                  isActive ? 'ap-service-btn--active' : '',
                  isDone   ? 'ap-service-btn--done'   : '',
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  if (btn.type === 'future') {
                    onFutureTopic?.(btn.id);
                  } else {
                    onSectionBtnClick?.(btn.id);
                  }
                }}
                disabled={chatLoading || !analysisReady}
                title={!analysisReady ? 'Analyze chart first' : btn.label}
              >
                {btn.label}
                {isDone && !isActive && (
                  <span className="ap-service-btn__check">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Section button bar — past mode only (built-in layout) ── */}
      {!serviceButtons && mode === 'past' && (
        <div className="ap-section-btns">
          {SECTION_BTNS.map((row, ri) => (
            <div key={ri} className="ap-section-btns__row">
              {row.map(btn => {
                const isDone    = sectionBtnDone?.has(btn.id);
                const isActive  = sectionBtnActive === btn.id;
                const isFuture  = btn.isFutureToggle;
                return (
                  <button
                    key={btn.id}
                    className={[
                      'ap-section-btn',
                      isActive  ? 'ap-section-btn--active' : '',
                      isDone    ? 'ap-section-btn--done'   : '',
                      btn.locked ? 'ap-section-btn--locked' : '',
                      isFuture  ? 'ap-section-btn--future'  : '',
                      !analysisReady && !btn.locked && !isFuture ? 'ap-section-btn--unready' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => {
                      if (isFuture) { switchMode('future'); }
                      else if (!btn.locked) { onSectionBtnClick?.(btn.id); }
                    }}
                    disabled={btn.locked || (!isFuture && chatLoading)}
                    title={btn.locked ? 'Unlocks after full reading' : btn.label}
                  >
                    {btn.label}
                    {isDone && !isActive && (
                      <span className="ap-section-btn__check"><CheckIcon /></span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ── Future Consultation UI — future mode only (built-in layout) ── */}
      {!serviceButtons && mode === 'future' && (
        <div className="ap-future-panel">
          {aiData && <FutureDashaCard dashaData={aiData.dashaData} />}

          <div className="ap-future-topics">
            {FUTURE_TOPICS.map((row, ri) => (
              <div key={ri} className="ap-future-topics__row">
                {row.map(btn => (
                  <button
                    key={btn.id}
                    className="ap-future-topic-btn"
                    onClick={() => {
                      if (btn.id === 'specificQuestion') {
                        inputRef.current?.focus();
                      } else {
                        onFutureTopic?.(btn.id);
                      }
                    }}
                    disabled={chatLoading}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <button
            className="ap-remedies-btn"
            onClick={() => onFutureTopic?.('remedies')}
            disabled={chatLoading}
          >
            💊 REMEDIES — Personalised Remedy Plan
          </button>
        </div>
      )}

      {/* ── Language bar — 5-language selector ── */}
      <div className="ap-lang-bar">
        {LANGUAGES.map(lang => (
          <button
            key={lang.id}
            className={`ap-lang-bar-btn${globalLang === lang.id ? ' ap-lang-bar-btn--active' : ''}`}
            onClick={() => onLangChange?.(lang.id)}
            title={lang.label}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* ── Messages area ── */}
      <div className="ap-chatbot__messages" ref={chatHistoryRef}>

        {/* Empty state */}
        {chatHistory.length === 0 && !chatLoading && (
          <div className="ap-chatbot__empty">
            <div className="ap-chatbot__empty-icon"><AISparkIcon /></div>
            <p>
              {serviceButtons
                ? 'Select a topic above or type your question'
                : mode === 'past'
                  ? 'Select a section above or type your question'
                  : 'Select a future consultation topic or type your question'}
            </p>
          </div>
        )}

        {/* Message bubbles */}
        {chatHistory.map(entry => (
          <React.Fragment key={entry.id}>

            {/* User bubble */}
            {entry.type === 'user' && (
              <div className="ap-chat-msg ap-chat-msg--user">
                <div className={`ap-chat-bubble ap-chat-bubble--user${detectConfirmation(entry.text) ? ' ap-chat-bubble--confirm' : ''}`}>
                  {entry.audioUrl ? <AudioBubble url={entry.audioUrl} sender="user" /> : entry.text}
                </div>
              </div>
            )}

            {/* AI structured bubble (or panchanga card) */}
            {entry.type === 'ai' && (
              <div className="ap-chat-msg ap-chat-msg--ai">
                {entry.section === 'panchanga'
                  ? <PanchangaCard entry={entry} />
                  : <AiBubble key={`${entry.id}_${entry.lang || 'te'}`} entry={entry} onVerify={onVerify} onRetranslate={onRetranslate} onFutureTopic={mode === 'future' ? onFutureTopic : undefined} />
                }
              </div>
            )}

            {/* System notice */}
            {entry.type === 'system' && (
              <div className="ap-chat-msg ap-chat-msg--system">
                <div className="ap-chat-bubble ap-chat-bubble--system">{entry.text}</div>
              </div>
            )}

          </React.Fragment>
        ))}

        {/* Typing indicator */}
        {chatLoading && (
          <div className="ap-chat-msg ap-chat-msg--ai">
            <div className="ap-chat-bubble ap-chat-bubble--ai ap-chat-bubble--typing">
              <span className="ap-typing-dot" />
              <span className="ap-typing-dot" />
              <span className="ap-typing-dot" />
            </div>
          </div>
        )}

      </div>

      {/* Error */}
      {chatError && <p className="ap-error ap-chatbot__error">{chatError}</p>}

      {/* ── Mobile: Analyze Chart button or Client Card (above input bar) ── */}
      {onMobileAnalyze && (
        <div className="ap-mobile-analyze-section ap-mobile-only">
          {/* Palm photo upload — visible for palmistry regardless of analysis state */}
          {serviceMode === 'palmistry' && onMobilePalmUpload && (
            <MobilePalmUpload
              onUpload={onMobilePalmUpload}
              rightUrl={rightHandPreviewUrl}
              leftUrl={leftHandPreviewUrl}
            />
          )}
          {!analysisReady ? (
            <>
              <button
                className={`ap-mobile-analyze-btn${analyzeLoading ? ' ap-mobile-analyze-btn--loading' : ''}`}
                onClick={onMobileAnalyze}
                disabled={analyzeLoading}
              >
                {analyzeLoading
                  ? '⏳ Starting...'
                  : serviceMode === 'palmistry' ? '⭐ Start Session' : '⭐ Analyze Chart'
                }
              </button>
              {analyzeError && (
                <p className="ap-mobile-analyze-error">{analyzeError}</p>
              )}
            </>
          ) : (
            <MobileClientCard
              clientData={clientData}
              aiData={aiData}
              rightHandUrl={rightHandPreviewUrl}
              leftHandUrl={leftHandPreviewUrl}
            />
          )}
        </div>
      )}

      {/* ── Bottom input bar ── */}
      <div className="ap-chat-input-bar">
        {isAudioRec ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: 'rgba(255,23,68,0.08)', borderRadius: 20, padding: '6px 12px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff1744', animation: 'vtdBlink 1s infinite', flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, height: 24 }}>
                {(audioWave || []).slice(0, 20).map((v, i) => (
                  <div key={i} style={{ width: 3, borderRadius: 2, background: '#ff1744', opacity: 0.6, height: `${Math.max(4, (v / 255) * 24)}px` }} />
                ))}
              </div>
              <span style={{ fontSize: 12, color: '#ff1744', fontWeight: 600 }}>
                {String(Math.floor(audioDuration / 60)).padStart(2, '0')}:{String(audioDuration % 60).padStart(2, '0')}
              </span>
              <button style={{ background: 'transparent', border: 'none', color: '#ff1744', fontSize: 13, cursor: 'pointer' }} onClick={cancelAudioRec}>Cancel</button>
            </div>
            <button className="ap-chat-send-btn" onClick={handleAudioSend} title="Send voice">
              <SendIcon />
            </button>
          </>
        ) : (
          <>
            <button
              className="ap-chat-mic-btn"
              onClick={handleMicClick}
              title="Record voice message"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', opacity: 0.7 }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
            <textarea
              ref={inputRef}
              className="ap-chat-input"
              value={chatQuestion}
              onChange={e => setChatQuestion(e.target.value)}
              onKeyDown={handleKey}
              placeholder={
                serviceButtons
                  ? 'Ask a question or use the buttons above...'
                  : mode === 'past'
                    ? 'Ask about past events, or type: Client confirmed: ...'
                    : 'Ask about future — type a custom question here...'
              }
              rows={2}
              maxLength={500}
              disabled={chatLoading}
            />
            <button
              className="ap-chat-send-btn"
              onClick={handleSend}
              disabled={!chatQuestion.trim() || chatLoading}
              title="Send">
              <SendIcon />
            </button>
          </>
        )}
      </div>
      <style>{`@keyframes vtdBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

    </main>
  );
};

export default AiChatbot;
